import type { ColumnContext } from "./valueGetters";
import type {
  CarrierColumn,
  FinancialsCoverageRef,
  FinancialsTableResponse,
  PatchFinancialsTableRequest,
  TotalsBlock,
} from "@platform/api";
import type { PendingCellValueRef } from "@platform/design-system";

export type CoverageRowKind =
  | "coverageHeader"
  | "annualPremium"
  | "variance"
  | "dollarSavings"
  | "notes";

export type TotalsRowKind =
  | "totalsHeader"
  | "totalAnnualPremium"
  | "totalVariance"
  | "totalDollarSavings"
  | "totalNotes"
  | "totalCredits"
  | "totalPremiumWithCredits";

export type FinancialsRowKind = CoverageRowKind | TotalsRowKind;

export const financialsRowKinds = {
  COVERAGE_HEADER: "coverageHeader",
  ANNUAL_PREMIUM: "annualPremium",
  VARIANCE: "variance",
  DOLLAR_SAVINGS: "dollarSavings",
  NOTES: "notes",
  TOTALS_HEADER: "totalsHeader",
  TOTAL_ANNUAL_PREMIUM: "totalAnnualPremium",
  TOTAL_VARIANCE: "totalVariance",
  TOTAL_DOLLAR_SAVINGS: "totalDollarSavings",
  TOTAL_NOTES: "totalNotes",
  TOTAL_CREDITS: "totalCredits",
  TOTAL_PREMIUM_WITH_CREDITS: "totalPremiumWithCredits",
} as const satisfies Record<string, FinancialsRowKind>;

/**
 * Row data carries identity and kind. `rowId` is the AG Grid row id,
 * `kind` discriminates which valueGetter / renderer to use, and
 * `coverageId` is present on coverage rows for grouping. Totals rows
 * carry a `blockId` (`'default'` for the implicit block, or a custom
 * block's UUID) so valueGetters can scope aggregation.
 */
export type FinancialsRow = {
  rowId: string;
  kind: FinancialsRowKind;
  coverageId?: string;
  blockId?: string;
  blockNames?: readonly string[];
};

/**
 * Stashed on `gridOptions.context` so valueGetters and cell renderers
 * can read the latest payload and look up the coverage a row belongs
 * to without going back through React state.
 */
export type FinancialsGridContext = {
  payload: FinancialsTableResponse;
  pendingRef: PendingCellValueRef;
  coverageByRowId: ReadonlyMap<string, string>;
  coverageById: ReadonlyMap<string, FinancialsCoverageRef>;
  carrierById: ReadonlyMap<string, CarrierColumn>;
  coverageIdsByBlock: ReadonlyMap<string, ReadonlySet<string>>;
  blockById: ReadonlyMap<string, TotalsBlock>;
  coverageIdToBlockName: ReadonlyMap<string, readonly string[]>;
  orphanedCoverageIds: ReadonlySet<string>;
  queueChange: (diff: PatchFinancialsTableRequest) => void;
  toggleCarrierHidden: (
    carrierId: string,
    currentlyHidden: boolean,
    carriers: readonly CarrierColumn[],
  ) => void;
  createTotalsBlock: (
    label: string,
    coverageIds: string[],
    position: number,
  ) => void;
  deleteTotalsBlock: (blockId: string) => void;
  /** Removes the analyst's override on a premium cell. */
  revertPremium: (coverageId: string, column: ColumnContext) => void;
  /** PPX_Financials_premium_overrides: whether analysts may edit or revert a premium. */
  premiumOverridesEnabled: boolean;
  collapsedCoverages: ReadonlySet<string>;
  toggleCoverageExpanded: (coverageId: string) => void;
};
